import { useEffect } from "react";
import { useLocation } from "wouter";

let firstRun = true;

// Back/forward: the browser restores the previous scroll position itself.
let poppedState = false;
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    poppedState = true;
  });
}

function readHashId(): string {
  const raw = window.location.hash.slice(1);
  try {
    return decodeURIComponent(raw);
  } catch {
    // Malformed percent-escape (e.g. #50%off): use the raw fragment.
    return raw;
  }
}

/**
 * Scrolls to the URL #hash once the route has rendered. The browser's own
 * hash scroll runs before the SPA renders, so links like /#contact from
 * another page would otherwise land at the top.
 *
 * In-app links without a hash start the new page at the top: the SPA keeps
 * the window scrolled where the previous page was otherwise.
 */
export function useHashScroll(routeKey: string) {
  const [pathname] = useLocation();

  useEffect(() => {
    const isFirstRun = firstRun;
    firstRun = false;
    const wasPop = poppedState;
    poppedState = false;
    const id = readHashId();
    if (!id) {
      if (!isFirstRun && !wasPop) window.scrollTo(0, 0);
      return;
    }
    if (isFirstRun) {
      // On reload or back/forward the browser restores the last scroll
      // position; don't replace it with a stale #hash.
      const navigation = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (navigation && navigation.type !== "navigate") return;
    }
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tryScroll = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ block: "start" });
      } else if (attempts++ < 20) {
        timer = setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
    return () => clearTimeout(timer);
  }, [routeKey, pathname]);
}
